import type { Edge, Node } from 'reactflow';

export type ObjectCallerEntry = {
  id: string;
  text: {
    id: string;
    text: string;
  }[];
};

export type ModifyDataFn = (
  node: Node[],
  id: string,
  objectData: { [key: string]: string },
  edges: Edge[],
  changeType?: string,
  objectCallerData?: ObjectCallerEntry[],
) => Node[];

export const PROPAGATION_CHANGE_TYPE = 'default';

export function collectOutgoingTargetIds(
  edges: Edge[] | undefined,
  sourceId: string,
): string[] {
  const out: string[] = [];
  for (const e of edges ?? []) {
    if (e.source === sourceId && e.target) {
      out.push(e.target);
    }
  }
  return [...new Set(out)];
}

export function propagateDownstreamFromSeeds(
  nodes: Node[],
  edges: Edge[],
  seedNodeIds: string[],
  modifyDataImpl: ModifyDataFn,
  objectCallerData?: ObjectCallerEntry[],
  propagationChangeType: string = PROPAGATION_CHANGE_TYPE,
): Node[] {
  const seen = new Set<string>();
  const queue = [...seedNodeIds];
  let current = nodes;
  const maxSteps = Math.max((nodes?.length ?? 0) + (edges?.length ?? 0), 1) * 2;
  let steps = 0;

  while (queue.length > 0 && steps < maxSteps) {
    const u = queue.shift();
    if (!u || seen.has(u)) {
      continue;
    }
    seen.add(u);
    steps += 1;
    current = modifyDataImpl(
      current,
      u,
      {},
      edges,
      propagationChangeType,
      objectCallerData,
    );
    for (const t of collectOutgoingTargetIds(edges, u)) {
      queue.push(t);
    }
  }

  return current;
}
